/**
 * 保险库 hooks（TanStack Query）：状态/文件夹/条目/历史。
 * 写操作（创建/更新条目）在客户端完成零知识加密：
 *   encryptItem(keys, payload) → 六字段（name/data 各 cipher/nonce/tag）展开进请求体，
 *   严禁携带任何 DTO 白名单之外的字段（后端 forbidNonWhitelisted）。
 * 解密在页面/组件侧用 lib/vault 的 decryptItem / decryptItemName 完成（明文不落 store）。
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createVaultFolder,
  createVaultItem,
  deleteVaultFolder,
  deleteVaultItem,
  getVaultItem,
  listVaultFolders,
  listVaultItemHistory,
  listVaultItems,
  markVaultItemUsed,
  updateVaultFolder,
  updateVaultItem,
  vaultStatus,
} from '../lib/api';
import { encryptItem } from '../lib/vault';
import { useVault } from '../stores/vault';
import type { VaultItemPayload, VaultItemUpdateBody } from '../lib/types';

/** 取内存密钥；未解锁时抛出可展示的中文错误 */
function requireKeys() {
  const keys = useVault.getState().keys;
  if (!keys) throw new Error('保险库已锁定，请先解锁');
  return keys;
}

export function invalidateVaultData(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['vaultStatus'] });
  qc.invalidateQueries({ queryKey: ['vaultFolders'] });
  qc.invalidateQueries({ queryKey: ['vaultItems'] });
  qc.invalidateQueries({ queryKey: ['vaultItem'] });
  qc.invalidateQueries({ queryKey: ['vaultItemHistory'] });
}

// ==================== 状态 ====================

export function useVaultStatus() {
  return useQuery({
    queryKey: ['vaultStatus'],
    queryFn: () => vaultStatus(),
    staleTime: 30_000,
  });
}

// ==================== 文件夹 ====================

export function useVaultFolders() {
  return useQuery({
    queryKey: ['vaultFolders'],
    queryFn: () => listVaultFolders(),
  });
}

export function useCreateVaultFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; icon?: string; parentId?: string }) => createVaultFolder(args),
    onSuccess: () => invalidateVaultData(qc),
  });
}

export function useUpdateVaultFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; name?: string; icon?: string | null; parentId?: string | null }) => {
      const { id, ...body } = args;
      return updateVaultFolder(id, body);
    },
    onSuccess: () => invalidateVaultData(qc),
  });
}

export function useDeleteVaultFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVaultFolder(id),
    onSuccess: () => invalidateVaultData(qc),
  });
}

// ==================== 条目（密文读写） ====================

export function useVaultItems(query?: {
  type?: string;
  folderId?: string;
  favorite?: string;
  archived?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['vaultItems', query ?? {}],
    queryFn: () => listVaultItems(query),
  });
}

export function useVaultItem(id: string | undefined) {
  return useQuery({
    queryKey: ['vaultItem', id],
    queryFn: () => getVaultItem(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { payload: VaultItemPayload; folderId?: string; favorite?: boolean }) => {
      const keys = requireKeys();
      const fields = await encryptItem(keys, args.payload);
      return createVaultItem({
        type: args.payload.data.type,
        ...(args.folderId !== undefined ? { folderId: args.folderId } : {}),
        ...(args.favorite !== undefined ? { favorite: args.favorite } : {}),
        ...fields,
      });
    },
    onSuccess: () => invalidateVaultData(qc),
  });
}

/**
 * 更新条目：payload 缺省时只改元数据（folderId/favorite/archivedAt）；
 * 传 payload 时重新加密并整体替换六字段（服务端自动留历史）。
 */
export function useUpdateVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      payload?: VaultItemPayload;
      folderId?: string | null;
      favorite?: boolean;
      archivedAt?: string | null;
    }) => {
      const body: VaultItemUpdateBody = {};
      if (args.payload) Object.assign(body, await encryptItem(requireKeys(), args.payload));
      if (args.folderId !== undefined) body.folderId = args.folderId;
      if (args.favorite !== undefined) body.favorite = args.favorite;
      if (args.archivedAt !== undefined) body.archivedAt = args.archivedAt;
      return updateVaultItem(args.id, body);
    },
    onSuccess: () => invalidateVaultData(qc),
  });
}

export function useDeleteVaultItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVaultItem(id),
    onSuccess: () => invalidateVaultData(qc),
  });
}

export function useMarkVaultItemUsed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markVaultItemUsed(id),
    onSuccess: () => invalidateVaultData(qc),
  });
}

// ==================== 历史 ====================

export function useVaultItemHistory(id: string | undefined, query?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: ['vaultItemHistory', id, query ?? {}],
    queryFn: () => listVaultItemHistory(id as string, query),
    enabled: Boolean(id),
  });
}
